import React, { useState } from 'react';

import TpDialog from '../shared/tp_dialog';
import { post_payout_generate } from '../api';
import {
  confirm_month_ok,
  downloads_text,
  generate_month_mismatch_text,
  generate_done_text,
  generate_view,
  new_round_note,
  regenerate_warning
} from './adapters';
import { KIND_NEW_ROUND, KIND_REGENERATE, generate_body } from './requests';
import useIdempotentPost from './use_idempotent_post';

/*
 * PROD-7828 W4 — confirm one generate: R1, Regenerate R<n> (cancels that file's unpaid lines; Ops must be
 * sure it never reached the bank) or a new round. Ops pick the month to confirm (S6, D44: a dropdown that starts blank;
 * Generate waits for a pick, and a month other than the screen's blocks with a hint). The request id is kept across a dropped answer, so
 * "Generate" again replays instead of making a second file.
 * props: pay_month, month_options ([{value, label}], generate_month_options), choice (generate_choices
 * item), eligible_count, eligible_total, on_close(),
 * on_done(text) after a file is made, on_stale(text) when the month moved on, on_error(e, fallback) (401 only).
 */
const FAILED_TEXT = 'The file may not have been made. Reload the files list to check.';

const GenerateDialog = ({
  pay_month,
  month_options,
  choice,
  eligible_count,
  eligible_total,
  on_close,
  on_done,
  on_stale,
  on_error
}) => {
  const [picked, setPicked] = useState(''); // D44: blank on every open (dialog remounts), so Ops must choose
  const [sure, setSure] = useState(false);
  const [error, setError] = useState('');
  const { busy, send } = useIdempotentPost(FAILED_TEXT);
  const regen = choice.kind === KIND_REGENERATE;
  const ok = confirm_month_ok(picked, pay_month) && (!regen || sure);
  const mismatch = generate_month_mismatch_text(picked, pay_month);

  const submit = () => {
    if (!ok || busy) return;
    const signature = generate_body({
      pay_month,
      confirm_pay_month: picked,
      client_request_id: '',
      choice
    });
    setError('');
    send(signature, (id) => post_payout_generate({ ...signature, client_request_id: id })).then(
      (r) => {
        if (r.ok) {
          on_done(generate_done_text(generate_view(r.data)));
          return;
        }
        if (r.err.kind === 'auth') on_error(r.raw, FAILED_TEXT);
        if (r.err.kind === 'stale') on_stale(r.err.text);
        else setError(r.err.text);
      }
    );
  };

  return (
    <TpDialog title={`${choice.label} for ${pay_month}`} on_close={on_close} busy={busy}>
      <div className="tp-modal tp-po-gen">
        <div className="tp-ctx">
          {eligible_count} {eligible_count === 1 ? 'teacher' : 'teachers'} can be paid now,{' '}
          {eligible_total} in total. The file pays exactly the teachers the server finds eligible
          when you confirm.
        </div>
        {regen ? (
          <>
            <div className="tp-banner tp-banner-warn" role="alert">
              {regenerate_warning(choice.file)}
            </div>
            <div className="tp-hint">{downloads_text(choice.file)}</div>
            <label className="tp-po-check" htmlFor="tp-po-gen-sure">
              <input
                id="tp-po-gen-sure"
                type="checkbox"
                checked={sure}
                onChange={(e) => setSure(e.target.checked)}
                disabled={busy}
              />
              I have checked R{choice.round_no} was not uploaded to the bank.
            </label>
          </>
        ) : null}
        {choice.kind === KIND_NEW_ROUND ? (
          <div className="tp-banner tp-banner-info">{new_round_note(choice.round_no)}</div>
        ) : null}
        <div className="tp-field">
          <label htmlFor="tp-po-gen-month">Month of the file</label>
          <select
            id="tp-po-gen-month"
            className="tp-select"
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
            disabled={busy}
          >
            <option value="">Pick the month</option>
            {(month_options || []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {mismatch ? <span className="tp-err">{mismatch}</span> : null}
        </div>
        {error ? (
          <div className="tp-neg" role="alert">
            {error}
          </div>
        ) : null}
        <div className="tp-modal-ft">
          <button type="button" className="tp-btn-ghost" onClick={busy ? undefined : on_close}>
            Cancel
          </button>
          <button
            type="button"
            className={regen ? 'tp-btn tp-btn-danger' : 'tp-btn'}
            onClick={submit}
            aria-disabled={!ok || busy ? 'true' : undefined}
          >
            {busy ? 'Making the file…' : choice.label}
          </button>
        </div>
      </div>
    </TpDialog>
  );
};

export default GenerateDialog;
